import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_URL } from '../lib/utils';

// Default permissions for unauthenticated or old users
const DEFAULT_PERMISSIONS = {
  can_edit_clients: false,
  can_validate_quote: false,
  can_manage_products: false,
  can_view_prices: true,
  can_manage_team: false,
  can_create_events: false,
  can_delete_quotes: false,
};

/// Auth Store
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      permissions: DEFAULT_PERMISSIONS,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Login failed');
          set({
            user: data,
            token: data.token,
            isAuthenticated: true,
            isLoading: false,
            permissions: data.permissions || DEFAULT_PERMISSIONS
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const res = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || 'Registration failed');

          if (data.is_active) {
            set({
              user: data,
              token: data.token,
              isAuthenticated: true,
              isLoading: false,
              permissions: data.permissions || DEFAULT_PERMISSIONS
            });
          } else {
            set({ isLoading: false });
          }
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        console.log("🔒 Déconnexion...");
        // Avec 'persist', le set mettra aussi à jour le localStorage automatiquement
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
          permissions: DEFAULT_PERMISSIONS
        });
        // On peut forcer un rechargement pour être sûr de nettoyer tous les états React
        // window.location.href = '/login'; 
      },

      checkAuth: async () => {
        const { token, logout } = get();
        // S'il n'y a pas de token, on est déjà déconnecté
        if (!token) return false;

        try {
          // On vérifie le token auprès du serveur
          const res = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!res.ok) {
            console.warn("⚠️ Session expirée ou invalide. Déconnexion automatique.");
            logout(); // Le token est invalide (401) ou user supprimé (404) -> On nettoie
            return false;
          }

          const userData = await res.json();
          // On met à jour les données utilisateur (au cas où le rôle ou les perms aient changé)
          set({ user: userData });
          return true;

        } catch (error) {
          console.error("Erreur vérification auth:", error);
          // En cas d'erreur réseau (serveur éteint), on peut choisir de déconnecter ou non.
          // Pour la sécurité, il vaut mieux déconnecter si on doute.
          // logout(); 
          return false;
        }
      },
      // Permission check helpers
      can: (permission) => {
        const perms = get().permissions || DEFAULT_PERMISSIONS;
        return perms[permission] === true;
      },

      isAdmin: () => {
        const user = get().user;
        return user?.role === 'admin';
      },

      getHeaders: () => {
        const token = get().token;
        return {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        };
      },
    }),
    { 
        name: 'eventpro-auth',
        // TO DO: on peut choisir de ne persister que le token pour plus de sécurité, 
        // mais persister le user permet un affichage immédiat (optimistic UI).
    }
  )
);

// Theme Store
export const useThemeStore = create(
  persist(
    (set) => ({
      theme: 'dark',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
      setTheme: (theme) => set({ theme }),
    }),
    { name: 'eventpro-theme' }
  )
);

// UI Store
export const useUIStore = create(
  persist(
    (set) => ({
      sidebarOpen: false,
      searchOpen: false,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      setSearchOpen: (open) => set({ searchOpen: open }),
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      // Tes nouvelles options de préférences
      showMetrics: true,
      showFollowupDate: false,

      toggleMetrics: () => set((state) => ({ showMetrics: !state.showMetrics })),
      toggleFollowupDate: () => set((state) => ({ showFollowupDate: !state.showFollowupDate })),
    }),
    {
      name: 'eventpro-ui-preferences', // Clé de stockage LocalStorage
      partialize: (state) => ({ // Optionnel: on ne persiste que les préférences, pas l'état du sidebar/search
        showMetrics: state.showMetrics,
        showFollowupDate: state.showFollowupDate
      }),
    }
  )
);

// Features Store (system config / feature flags)
export const useFeaturesStore = create(
  persist(
    (set) => ({
      crm: false,
      gold: false,
      loaded: false,
      loadFeatures: async () => {
        try {
          const res = await fetch(`${API_URL}/system/config`);
          if (res.ok) {
            const data = await res.json();
            set({ crm: data.crm_enabled === true, gold: data.gold_enabled === true, loaded: true });
          } else {
            set({ loaded: true });
          }
        } catch {
          set({ loaded: true });
        }
      },
    }),
    { name: 'eventpro-features' }
  )
);
